import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "./ui/card";
import {
  AlertCircle,
  Loader,
  Phone,
  Info,
  Check,
  Camera,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";
import giftTime from "../assets/images/gift-time.svg";
import BuyerInfoModal from "./BuyerInfoModal";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";
import insurancePolicyService from "../lib/InsurancePolicyService";
import confetti from "canvas-confetti";
import ReinTcpaDisclosure from "./common/ReinTcpaDisclosure";

// Feature flag: set to false to rollback to old offer-only layout
const COMBINED_OFFER_UPLOAD = true;

// Redirect to login on 401, preserving current URL for post-auth return
const handle401 = () => {
  sessionStorage.setItem(
    "redirectAfterAuth",
    window.location.pathname + window.location.search,
  );
  const path = window.location.pathname;
  if (path.startsWith("/extended-warranty"))
    window.location.href = "/extended-warranty/welcome";
  else if (path.startsWith("/renewal"))
    window.location.href = "/renewal/welcome";
  else window.location.href = "/onboarding/welcome";
};

// ─── Shared sub-components ──────────────────────────────────────────

const BuyerMatchCard = ({
  buyerData,
  stepName,
  nudge,
  whyMatchOpen,
  setWhyMatchOpen,
  trackButtonClick,
  setShowBuyerModal,
  isViu,
}) => (
  <div className="border border-gray-200 rounded-2xl overflow-hidden">
    <div className="flex items-center gap-4 p-4">
      <div
        className={`w-14 h-14 rounded-xl flex items-center justify-center p-2 flex-shrink-0 ${
          isViu ? "" : "bg-violet-100"
        }`}
      >
        <img
          className="w-full h-full object-contain"
          src={buyerData.logo}
          alt={buyerData.name}
        />
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-base font-bold text-darkPurple">
          {buyerData.name}
        </h2>
        <p className="text-sm text-gray-500">
          Matched to your profile · Verified by Ownli
        </p>
      </div>
      <a
        onClick={() => {
          trackButtonClick("buyer-detail-modal", { step_name: stepName });
          setShowBuyerModal(true);
        }}
        className="text-[#7B61FF] font-medium cursor-pointer text-sm flex-shrink-0"
      >
        Learn more
      </a>
    </div>

    <div className="border-t border-gray-200" />

    <div className="p-4">
      <button
        onClick={() => setWhyMatchOpen((prev) => !prev)}
        className={`flex items-center justify-between w-full text-left${
          nudge ? " animate-nudge" : ""
        }`}
      >
        <p className="text-xs uppercase tracking-wide font-bold text-gray-500">
          Why <span className="normal-case">{buyerData.name}</span> matched with
          you
        </p>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            whyMatchOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-200 ${
          whyMatchOpen ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0"
        }`}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span className="text-lg flex-shrink-0">🎯</span>
            <p className="text-sm">
              <span className="font-bold">
                Your profile fits their specialty.
              </span>{" "}
              {buyerData.name} works with carriers best suited for drivers with
              your coverage history and location.
            </p>
          </div>
          {!(
            buyerData.name?.toLowerCase() === "viu" ||
            buyerData.name?.toLowerCase() === "viu by hub"
          ) && (
            <div className="flex items-start gap-3">
              <span className="text-lg flex-shrink-0">💰</span>
              <p className="text-sm">
                <span className="font-bold">They compete, not just quote.</span>{" "}
                {buyerData.name} works with some of the best carriers to find
                the most competitive rate. They're incentivized to win your
                business.
              </p>
            </div>
          )}
          {!(
            buyerData.name?.toLowerCase() === "viu" ||
            buyerData.name?.toLowerCase() === "viu by hub"
          ) && (
            <div className="flex items-start gap-3">
              <span className="text-lg flex-shrink-0">💸</span>
              <p className="text-sm">
                <span className="font-bold">They paid to reach you.</span>{" "}
                Unlike traditional leads, {buyerData.name} will pay you for
                access to your verified profile and part of that goes directly
                to you.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

const WhatsNextCard = ({ buyerName, amount, hideAmount }) => (
  <div className="border border-gray-200 rounded-2xl p-5 space-y-4">
    <p className="text-xs uppercase tracking-wide font-bold text-gray-500">
      What Happens Next
    </p>
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-green-700 text-sm font-bold">1</span>
        </div>
        <p className="text-sm">
          <span className="font-bold">{buyerName} gets to work</span> — they'll
          build a real, bindable quote from your verified profile.
        </p>
      </div>
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-green-700 text-sm font-bold">2</span>
        </div>
        <p className="text-sm">
          <span className="font-bold">Call them now or wait</span> — {buyerName}{" "}
          will follow up within one business day if you prefer.
        </p>
      </div>
      {!hideAmount && (
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-green-700 text-sm font-bold">3</span>
          </div>
          <p className="text-sm">
            <span className="font-bold">
              Your ${amount.toFixed(0)} is released
            </span>{" "}
            — directly to you once your profile is approved.
          </p>
        </div>
      )}
    </div>
  </div>
);

const OfferAmountCard = ({ amount, label, hideAmount, customContent }) => (
  <div className="bg-[#D4F6C9] rounded-2xl p-5">
    <p className="text-xs uppercase tracking-wide font-bold mb-3 text-gray-600">
      {label}
    </p>
    {customContent ? (
      <p className="text-sm text-gray-700">{customContent}</p>
    ) : (
      <div className="flex items-stretch">
        {!hideAmount && (
          <div className="flex-1 pr-4">
            <p className="text-3xl font-bold font-bebas text-darkPurple">
              ${amount.toFixed(0)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Cash earned — your data, your money
            </p>
          </div>
        )}
        <div
          className={
            hideAmount ? "flex-1" : "border-l border-gray-400 pl-4 flex-1"
          }
        >
          <p className="text-3xl font-bold font-bebas text-darkPurple">
            $1,000+
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Estimated potential annual savings
          </p>
        </div>
      </div>
    )}
  </div>
);

const BuyerOfferScreen = ({
  buyerData,
  onAcceptOffer,
  onReject,
  onUpload,
  onQuickConnect,
  renewal = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [error, setError] = useState(null);
  const [policyStatus, setPolicyStatus] = useState("checking"); // "checking" | "none" | "exists"
  const [whyMatchOpen, setWhyMatchOpen] = useState(false);
  const { getIdToken } = useAuth();
  const { trackButtonClick } = useInteractionTracking();
  const headerRef = useRef(null);

  // Check server for existing policy on mount
  useEffect(() => {
    const checkPolicy = async () => {
      try {
        const token = await getIdToken();
        const result = await insurancePolicyService.checkExistingPolicies(
          token,
        );
        if (
          result &&
          ((Array.isArray(result) && result.length > 0) || result.policy)
        ) {
          setPolicyStatus("exists");
        } else {
          setPolicyStatus("none");
        }
      } catch (err) {
        console.error("Error checking existing policies:", err);
        if (err.message?.includes("401")) {
          handle401();
          return;
        }
        setPolicyStatus("none"); // Default to showing upload if check fails
      }
    };
    checkPolicy();
  }, [getIdToken]);

  // Fire confetti burst near the header when pre-upload view appears
  useEffect(() => {
    if (policyStatus !== "none" || !headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { x, y },
      colors: ["#7B61FF", "#1B1464", "#CEC5FF", "#FFD700", "#FF6B6B"],
    });
  }, [policyStatus]);

  useEffect(() => {
    if (policyStatus === "checking") return;
    const viewName =
      policyStatus === "exists"
        ? "buyer_offer_post_upload"
        : "buyer_offer_pre_upload";
    analyticsService.trackPageView(viewName, {
      buyer: buyerData?.name,
      combined_layout: COMBINED_OFFER_UPLOAD,
      policy_status: policyStatus,
    });
    if (policyStatus === "exists") {
      analyticsService.trackEvent("offer_unlocked", {
        buyer: buyerData?.name,
      });
    }
  }, [policyStatus]);

  if (!buyerData) return null;
  // Check if the buyer is SimplyIOA
  const isSimplyIOA = buyerData.name === "SimplyIOA";
  const isAADirect = buyerData.name?.toLowerCase() === "aadirect";
  const isRein = buyerData.name?.toLowerCase() === "rein";
  const isNovo = buyerData.name?.toLowerCase() === "novo";
  const isViu =
    buyerData.name?.toLowerCase() === "viu" ||
    buyerData.name?.toLowerCase() === "viu by hub";

  const handleOfferResponse = async (accepted) => {
    if (isSimplyIOA) {
      // For SimplyIOA, just continue to consent screen without API call
      onAcceptOffer(buyerData);
      return;
    }

    if (isViu) {
      // VIU has no reward — skip /accept and go straight to the VIU choice screen
      onAcceptOffer(buyerData);
      return;
    }

    // For other buyers, make API call to accept offer and process reward
    setIsProcessing(true);
    setError(null);

    try {
      // Get authentication token
      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      // Make API call to accept the offer
      let acceptOfferUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/offers/${buyerData.id}/accept`;
      if (renewal) {
        // Retrieve the selected policy from sessionStorage
        const selectedPolicyStr = sessionStorage.getItem(
          "selectedRenewalPolicy",
        );
        const selectedPolicy = selectedPolicyStr
          ? JSON.parse(selectedPolicyStr)
          : null;

        acceptOfferUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/renewal/offers/${buyerData.id}/accept`;
        // Add the policy ID to the request parameters or body
        if (selectedPolicy && selectedPolicy.id) {
          acceptOfferUrl += `?policy=${selectedPolicy.id}`;
        }
      }
      const response = await fetch(acceptOfferUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
        body: JSON.stringify({
          dataBuyerId: buyerData.dataBuyerId,
          amount: buyerData.offer.amount,
        }),
      });

      if (response.status === 401) {
        handle401();
        return;
      }
      if (!response.ok) {
        throw new Error("Something went wrong. Please try again.");
      }

      // Parse the response to get reward data
      const primaryReward = await response.json();

      // Signal Rein about the new lead (awaited so it completes while loader is visible)
      if (isRein) {
        try {
          const reinResponse = await fetch(
            `${API_CONFIG.BASE_URL}/api/rein/create-offer`,
            {
              method: "POST",
              headers: {
                "x-user-token": token,
              },
            },
          );
          if (reinResponse.status === 401) {
            handle401();
            return;
          }
        } catch (err) {
          console.error("Rein create-offer failed:", err);
        }
      }

      const rewardData = {
        id: primaryReward.id,
        dataBuyerId: primaryReward.buyer?.id,
        name: primaryReward.buyer?.name,
        logo: primaryReward.buyer?.logo,
        listLogo: primaryReward.buyer?.listLogo,
        hasExistingReward: true, // Flag to indicate this is an existing reward
        isAADirect: isAADirect,
        isRein: isRein,
        isViu: isViu,
        offer: {
          amount: primaryReward.amount,
          currency: "USD",
        },
        description: primaryReward.buyer?.description,
        shortDescription: primaryReward.buyer?.shortDescription,
        shopNowLink: primaryReward.buyer?.shopNowLink,
        allRewards: [primaryReward],
        timestamp: new Date().toISOString(),
      };
      // Continue to next screen with buyer data
      onAcceptOffer(rewardData);
    } catch (err) {
      console.error("Error accepting offer:", err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── NEW COMBINED OFFER + UPLOAD LAYOUT ─────────────────────────────
  if (COMBINED_OFFER_UPLOAD) {
    // Show loading while checking policy status
    if (policyStatus === "checking") {
      return (
        <div className="flex items-center justify-center p-12">
          <Loader className="w-8 h-8 text-[#7B61FF] animate-spin" />
        </div>
      );
    }

    // ── POST-UPLOAD: "Offer Unlocked" view ──
    if (policyStatus === "exists") {
      return (
        <div className="space-y-6">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {/* Success banner */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold font-bebas uppercase">
              Congrats!
            </h1>
            <p className="text-gray-600">
              {isViu
                ? "Your document has been uploaded. Get Quotes. Save."
                : "Your document has been uploaded. Accept. Earn. Save."}
            </p>
          </div>

          <BuyerMatchCard
            buyerData={buyerData}
            stepName="buyer_offer_unlocked"
            whyMatchOpen={whyMatchOpen}
            setWhyMatchOpen={setWhyMatchOpen}
            trackButtonClick={trackButtonClick}
            isViu={isViu}
            setShowBuyerModal={setShowBuyerModal}
          />
          <BuyerInfoModal
            open={showBuyerModal}
            onOpenChange={setShowBuyerModal}
            buyer={buyerData}
          />

          {!isViu && (
            <OfferAmountCard
              amount={buyerData.offer.amount}
              label="Accept Offer"
              hideAmount={isViu}
            />
          )}

          {/* Rein/AADirect action required callout */}
          {isAADirect && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Phone className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-yellow-900">Action Required</h3>
                  <p className="text-sm text-yellow-800">
                    Call a {buyerData.name} expert to complete your quote and
                    activate your reward
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Accept Offer box — styled like Unlock Your Reward */}
          <div className="border-2 border-[#7B61FF] rounded-2xl p-5 space-y-3 bg-[#F5F2FF]">
            {!isViu && (
              <p className="text-[#7B61FF] font-bold text-sm uppercase tracking-wide">
                Accept Offer
              </p>
            )}
            <p className="text-gray-500 text-sm">
              {isViu
                ? "Compare multiple insurance quotes in seconds from 50+ A-rated carriers"
                : `Accept your offer to earn $${buyerData.offer.amount.toFixed(
                    0,
                  )} and save $1,000+`}
            </p>
            <button
              onClick={() => {
                trackButtonClick("accept-offer", {
                  step_name: "buyer_offer_unlocked",
                });
                handleOfferResponse(true);
              }}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
            >
              {isProcessing ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : isViu ? (
                "Get Quotes"
              ) : (
                "Accept Offer"
              )}
            </button>
            {isRein ? (
              <ReinTcpaDisclosure buttonLabel="Accept Offer" />
            ) : isViu ? (
              <p className="text-sm text-center text-gray-600">
                By clicking "Get Quotes", you agree: (1) to your profile
                information being sent to VIU by HUB for use according to its{" "}
                <a
                  href="https://www.viubyhub.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Privacy Policy
                </a>
                , and (2) that VIU by HUB may contact you via email or telephone
                call about your insurance needs. You can opt-out of marketing
                communications at any time.
              </p>
            ) : (
              <p className="text-xs text-center text-gray-400">
                By proceeding, you agree {buyerData.name} may contact you. You
                can opt out at any time.
              </p>
            )}
          </div>

          <WhatsNextCard
            buyerName={buyerData.name}
            amount={buyerData.offer.amount}
            hideAmount={isViu}
          />
        </div>
      );
    }

    // ── PRE-UPLOAD: upload-focused layout ──
    return (
      <div className="space-y-6">
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center space-y-3" ref={headerRef}>
          <h1 className="text-2xl font-bold font-bebas uppercase">
            You're one step away from your offer.
          </h1>
        </div>

        <BuyerMatchCard
          buyerData={buyerData}
          stepName="buyer_offer_combined"
          nudge
          whyMatchOpen={whyMatchOpen}
          setWhyMatchOpen={setWhyMatchOpen}
          trackButtonClick={trackButtonClick}
          setShowBuyerModal={setShowBuyerModal}
          isViu={isViu}
        />
        <BuyerInfoModal
          open={showBuyerModal}
          onOpenChange={setShowBuyerModal}
          buyer={buyerData}
        />

        <OfferAmountCard
          amount={buyerData.offer.amount}
          label="What You Unlock"
          hideAmount={isViu}
          customContent={
            isViu
              ? "Compare multiple insurance quotes in seconds from 50+ A-rated carriers"
              : null
          }
        />

        {/* UNLOCK YOUR REWARD card */}
        <div className="border-2 border-[#7B61FF] rounded-2xl p-5 space-y-3 bg-[#F5F2FF]">
          <p className="text-[#7B61FF] font-bold text-sm uppercase tracking-wide">
            {isViu ? "🔒 Get Your Quotes" : "🔒 Unlock Your Reward"}
          </p>
          <h2 className="text-lg font-bold text-darkPurple">
            Upload your insurance card or declaration page
          </h2>
          <p className="text-gray-500 text-sm">
            Takes 30 seconds. Verifies your profile so {buyerData.name} can
            deliver a real, bindable quote.
          </p>

          <button
            onClick={() => {
              trackButtonClick("take-photo-upload", {
                step_name: "buyer_offer_combined",
              });
              onUpload();
            }}
            className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
          >
            <Camera className="w-5 h-5" />
            Take a photo to upload
          </button>
        </div>

        {((isRein && buyerData.shopNowLink) || isViu) && onQuickConnect && (
          <div className="border-2 border-[#7B61FF]/30 bg-[#F5F2FF]/50 rounded-2xl p-5 text-center space-y-3">
            <p className="text-gray-700 font-semibold text-sm">
              Don't have it handy?
            </p>
            <p className="text-gray-500 text-xs">
              {isViu
                ? `Upload your document — or go straight to ${buyerData.name}.`
                : `Upload to earn your reward — or go straight to ${buyerData.name}.`}
            </p>
            <button
              onClick={() => {
                trackButtonClick("skip-upload-quick-connect", {
                  step_name: "buyer_offer_combined",
                  buyer: buyerData.name,
                });
                onQuickConnect(buyerData);
              }}
              className="inline-flex items-center gap-1 text-[#7B61FF] font-bold text-sm border border-[#7B61FF] rounded-lg px-4 py-2 hover:bg-[#7B61FF] hover:text-white transition-colors"
            >
              Continue without uploading &rarr;
            </button>
          </div>
        )}

        <WhatsNextCard
          buyerName={buyerData.name}
          amount={buyerData.offer.amount}
          hideAmount={isViu}
        />

        {/* Consent footer */}
        <p className="text-xs text-center text-gray-400">
          By proceeding, you agree {buyerData.name} may contact you. You can opt
          out at any time.
        </p>
      </div>
    );
  }

  // ─── BEGIN OLD LAYOUT (rollback: set COMBINED_OFFER_UPLOAD = false) ──
  return (
    <div className="space-y-6 flex flex-col h-[calc(100vh-110px)]">
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-xl font-bold uppercase">
          You've Got an Offer from
        </h1>
        <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto !my-2 p-3">
          <img
            className="w-full h-full object-contain"
            src={buyerData.logo}
            alt="buyerLogo"
          />
        </div>
        <h1 className="text-xl uppercase font-bold text-darkPurple !mt-0">
          {buyerData.name}
        </h1>
        <a
          onClick={() => {
            trackButtonClick("buyer-detail-modal", {
              step_name: "buyer_offer",
            });
            setShowBuyerModal(true);
          }}
          className="underline text-[#1B1B1E]"
        >
          Learn more
        </a>
        <BuyerInfoModal
          open={showBuyerModal}
          onOpenChange={setShowBuyerModal}
          buyer={buyerData}
        />
        <p className="text-lg !mt-6">
          They are excited to work with you and would like to access your
          verified Ownli Insurance Profile and offer you a competitive rate.
        </p>
      </div>
      {!(isAADirect || isRein || isSimplyIOA || isNovo || isViu) && (
        <p className="text-center text-lg !mt-4">
          {buyerData.shortDescription ||
            "They've helped thousands of people save on insurance across 48 states."}
        </p>
      )}
      <Card className="shadow-none bg-[#CEC5FF66] border-none !mt-6 rounded-[20px] !mb-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <img src={giftTime} alt="giftTime" />
            <p className="text-base text-darkPurple">
              Your reward is pending until your profile is approved
            </p>
          </div>
        </CardContent>
      </Card>
      <div className="w-[174px] w-fit mx-auto flex items-center justify-center p-5 bg-lightGrey rounded-[20px] !my-4">
        <h2 className="text-xl font-bold">
          ${buyerData.offer.amount.toFixed(2)}
        </h2>
      </div>

      {/* AADirect/Rein specific message */}
      {isAADirect && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6 !mt-4 !mb-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Phone className="w-8 h-8 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-yellow-900 mb-2">
                ⚡ Action Required
              </h3>
              <p className="text-base text-yellow-800 font-medium">
                Call an {buyerData.name} expert to complete your quote and
                activate your reward
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="!mt-auto space-y-3">
        <button
          onClick={() => {
            trackButtonClick("view-offer", {
              step_name: "buyer_offer",
            });
            handleOfferResponse(true);
          }}
          disabled={isProcessing}
          className={`w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors`}
        >
          {isProcessing ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              {isSimplyIOA
                ? "Continue"
                : isAADirect || isRein || isViu
                ? "Accept & Get Call Info"
                : "Accept Offer"}
            </>
          )}
        </button>
        {isRein && <ReinTcpaDisclosure buttonLabel="Accept & Get Call Info" />}
        {isViu && (
          <p className="text-xs text-center text-gray-500">
            By clicking on the above button "ACCEPT AND GET CALL INFO", you
            agree to be contacted by {buyerData.name} by phone or e-mail.
            Message and data rates may apply. You can opt out at any time.
          </p>
        )}
      </div>
    </div>
  );
  // ─── END OLD LAYOUT ──────────────────────────────────────────────────
};

export default BuyerOfferScreen;
